import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Icon, Loading } from "@/deps/ui-library";
import { api } from "@/api";
import { keys } from "@/api/queries";
import type { CheckoutResult } from "@/api/types";

/** Where eSewa, Khalti and connectIPS send the customer back (suite ADR-0102); the API gives the gateway this address. */
export const PAYMENT_RETURN_PATH = "/settings/subscription/paid";

type Outcome =
  | { kind: "checking" }
  | { kind: "paid"; result: CheckoutResult }
  | { kind: "declined"; message: string }
  | { kind: "refused"; message: string };

/**
 * Back from a gateway's page (suite ADR-0102). The query string it came back with goes to RigoSub as it is: RigoSub finds
 * the order from it and asks the gateway, so nothing here believes the address. Asked once; a reload asks again and
 * RigoSub answers the same for an order already paid.
 *
 * It renders inside the shell, and also in place of the locked-books page, since a suspended company pays its bill here.
 */
export function PaymentReturnPage({
  search = typeof window === "undefined" ? "" : window.location.search,
  onDone,
}: {
  search?: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<Outcome>({ kind: "checking" });
  const asked = useRef(false);

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    api.subscription
      .confirmReturn(search)
      .then((result) => {
        if (result.outcome === "PAID") {
          setOutcome({ kind: "paid", result });
          void queryClient.invalidateQueries({ queryKey: keys.subscription });
          return;
        }
        setOutcome({
          kind: "declined",
          message: result.message ?? "The payment didn't go through. Nothing was charged.",
        });
      })
      .catch((failure: unknown) =>
        setOutcome({
          kind: "refused",
          message:
            failure instanceof Error ? failure.message : "The payment couldn't be confirmed.",
        }),
      );
  }, [search, queryClient]);

  return (
    <div className="flex justify-center p-4">
      <Card className="flex w-full max-w-lg flex-col gap-4 p-6">
        {outcome.kind === "checking" ? (
          <div role="status" className="flex flex-col items-center gap-3 py-6">
            <Loading />
            <span className="text-fg-muted text-sm">
              Confirming the payment with the provider. This takes a few seconds.
            </span>
          </div>
        ) : outcome.kind === "paid" ? (
          <>
            <div className="flex items-center gap-2">
              <Icon name="CircleCheck" />
              <h2 className="text-fg m-0 text-lg font-semibold">Payment received</h2>
            </div>
            <p className="text-fg m-0 text-sm">
              {outcome.result.billId
                ? `Bill ${outcome.result.billId} is paid. The tax invoice comes from Rigo Billing by email.`
                : "It's paid. The tax invoice comes from Rigo Billing by email."}
            </p>
          </>
        ) : (
          <Alert
            config={{
              variant: "subtle",
              colorScheme: outcome.kind === "declined" ? "warning" : "danger",
            }}
            title={
              outcome.kind === "declined"
                ? "The payment didn't go through"
                : "The payment couldn't be confirmed"
            }
            description={`${outcome.message} You can try again from Settings › Subscription.`}
          />
        )}
        {outcome.kind !== "checking" ? (
          <div className="flex justify-end">
            <Button colorScheme="primary" size="sm" onClick={onDone}>
              Go to subscription
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
